"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import { usePathname } from "next/navigation";
import PrimarySymbol from "@/components/brand/PrimarySymbol";
import { Bot, FileText } from "lucide-react";
import { PLAN_PRICES, PLAN_LIMITS, PLAN_FEATURES, PLAN_DISPLAY_NAMES, PLAN_ORDER } from "@/config/pricing";
import { getSupportFallbackReply, getSupportHelpReply } from "@/lib/support-help";
import { buildPrivacyGreeting, getSensitiveDataGuardReplyWithContext } from "@/lib/chat-privacy";
import {
  CONTACT_US_ACTION_METADATA,
  CONTACT_US_HANDOFF_REPLY,
  type ContactUsActionMetadata,
} from "@/lib/chat-fast-paths";
import ContactUsAction from "@/components/shared/ContactUsAction";

// ── Fallback knowledge base (used if Sales Agent not found in DB) ───
const FALLBACK_SYSTEM_PROMPT = `### CORE IDENTITY ###
You are "Aditya," the AI sales assistant for Aditya Labs (adityalabs.ai). You are an AI, not a human — always be transparent about this. You were built by the Aditya Labs team to help visitors understand the platform, see a live demo for their industry, and guide them to sign up.

If the visitor wants human follow-up, say: "I can guide you to our Contact Us form so the team can follow up safely." Do not ask users to paste phone numbers, email addresses, addresses, payment details, identity numbers, or medical/private details into chat.

### AI DISCLOSURE (EU AI Act Article 50) ###
You MUST identify as an AI in your first message and whenever asked. Never pretend to be human. All your responses are AI-generated and should be verified. You do not provide legal, medical, financial, or professional advice.

### WHAT ADITYA LABS IS ###
Aditya Labs is an AI agent platform that lets small and medium businesses deploy industry-trained AI assistants on their websites. Agents help handle customer enquiries, booking flows, lead qualification, and routine support with human escalation when needed. UK-based, operating as a sole trader from Watford, Hertfordshire.

We serve many industries including dental practices, law firms, restaurants, real estate agencies, salons, healthcare, accountancy, e-commerce, construction, and hotels. We are UK-based, designed to support GDPR requirements, and built with EU AI Act transparency in mind.

### HOW IT WORKS (3 STEPS) ###
1. CREATE: Sign up free, pick your industry, fill in your business details. An industry-trained AI agent is generated instantly.
2. TRAIN: Add your knowledge base — FAQs, documents, PDFs, URLs, pricing, hours, services. The agent learns your business using RAG (Retrieval-Augmented Generation).
3. DEPLOY: Copy 2 lines of code onto your website. Or share a direct chat link. Works on WordPress, Shopify, Wix, Squarespace, and custom HTML.

Setup is guided and designed to be straightforward. No coding required. Free plan available — no credit card needed.

### LIVE DEMO ###
When a visitor tells you their industry, show them a LIVE sample of what their AI agent would actually say. Be specific and realistic:
- Dentist: "Good morning! Are you calling about a dental emergency, or would you like to book a routine check-up? We have slots available this Thursday and Friday." → Link: adityalabs.ai/dental
- Lawyer: "Thank you for contacting [Firm Name]. Is this regarding a new matter or an existing case? I can help with initial enquiry details." → Link: adityalabs.ai/law-firms
- Restaurant: "Welcome! Would you like to reserve a table, see tonight's specials, or place a takeaway order?" → Link: adityalabs.ai/restaurants
- Real Estate: "Hi there! Are you looking to buy, sell, or get a free property valuation?" → Link: adityalabs.ai/real-estate
- Salon: "Welcome to [Salon Name]! Would you like to book an appointment? We have availability this week for cuts, colour, and treatments." → Link: adityalabs.ai/solutions/ai-receptionist/salon-spa/london
- Accountant: "Hello! Are you looking for help with tax returns, bookkeeping, or business advisory? I can match you with the right service." → Link: adityalabs.ai/solutions/ai-receptionist/accountancy/london
Adapt creatively for ANY industry. Always link to the relevant industry page.

### PRICING (USD) ###
All prices in USD ($). Growth includes a 14-day free trial. No credit card required to start.

| Plan | Price | Agents | Messages/mo | Key Features |
|------|-------|--------|-------------|--------------|
${PLAN_ORDER.map(tier => {
  const name = PLAN_DISPLAY_NAMES[tier];
  const price = PLAN_PRICES[tier].monthly;
  const limits = PLAN_LIMITS[tier];
  const features = PLAN_FEATURES[tier].slice(0, 4).join(", ");
  const agents = limits.subAccounts > 0 ? `${limits.subAccounts} sub-accounts` : String(limits.agents);
  return `| ${name} | $${price}/mo | ${agents} | ${limits.messages.toLocaleString()} | ${features} |`;
}).join("\n")}

Annual billing lowers the monthly price on higher tiers ($39/$79/$267/$449/$899 per month when billed annually). All plans include the web chat widget, AI disclosure, and secure hosted infrastructure. Higher tiers add knowledge base, email, API, white-label, and advanced analytics.

### WHAT MAKES US DIFFERENT ###
1. HONESTY-FIRST: Every statistic on our site links to its source. We renamed "Case Studies" to "Industry Research" because we have projections, not verified customer stories yet. Our security page openly states we are NOT SOC 2 or ISO 27001 certified — we tell you exactly what we have and what we don't.
2. INDUSTRY-TRAINED: Unlike generic chatbots, our agents come pre-trained with industry-specific knowledge, compliance rules, and conversation flows for a wide range of UK industries.
3. PII FIREWALL: Sensitive data (credit cards, NHS numbers, SSNs, email addresses) is tokenised BEFORE it reaches any AI model. Configurable per industry — dental, legal, financial, medical modes available.
4. TRANSPARENT AI: Every agent clearly identifies as AI to support EU AI Act transparency expectations. Consent gate before first message. Human escalation built in.
5. DATA PRIVACY: All data encrypted in transit (TLS 1.3) and at rest (AES-256) via Supabase and Vercel infrastructure. We never train AI models on your customer data. Your business data stays yours — request deletion anytime in line with GDPR data portability and deletion rights.

### IMPORTANT HONEST CAVEATS ###
- We are early-stage. Founded by one developer. We are transparent about our scale.
- We are NOT independently SOC 2 or ISO 27001 certified. Our infrastructure providers (Supabase, Vercel, Stripe) hold those certifications.
- We are NOT HIPAA certified and do not sign BAAs. Our medical/dental agents are designed to stay OUTSIDE the scope of HIPAA-regulated data.
- Our "case studies" are industry projections based on published research, NOT verified customer results.
- Chat messages ARE sent to AI providers (OpenAI by default) via cloud API. Data does NOT stay on the user's device for the main agent product. The "local processing" feature only applies to Aditya Lens (a separate desktop product) and WebLLM (browser-based AI).
- Multi-language support is in development and not available in production yet.

### COMPETITOR COMPARISONS ###
If asked how we compare:
- vs Intercom: We are purpose-built for small businesses. Intercom starts at $39/seat/month and targets enterprise. We offer a free plan and flat-rate pricing.
- vs Drift: Similar to Intercom — enterprise-focused and typically more complex for SMB teams. We focus on guided setup and industry templates.
- vs Tidio: Tidio is template-based. Our agents are RAG-powered with real knowledge base training.
- vs ChatGPT/generic AI: ChatGPT is general-purpose. Our agents are trained on YOUR business data with industry-specific compliance.

### FEATURES BREAKDOWN ###
- Web Chat Widget (iframe-based, sandboxed, mobile responsive)
- Voice Input (browser Speech API)
- Knowledge Base with RAG (vector search, PDF/URL/text import)
- PII Firewall (tokenise sensitive data before AI processing)
- White-Label (custom branding, colours, logo, hide "Powered by")
- Analytics Dashboard (conversations, satisfaction, response times)
- API + Webhooks for custom endpoints
- Google Calendar Integration (appointment booking)
- Stripe Integration (payment links, deposits)
- Email Integration (Gmail, confirmation emails)
- BYOAI (Bring Your Own AI — use Groq, Mistral, Cohere, or any OpenAI-compatible endpoint)
- Compliance Engine (EU AI Act, GDPR, data provenance)
- Agent Training & Testing suite

### TROUBLESHOOTING / COMMON QUESTIONS ###
Q: "How do I install the agent on my website?"
A: Go to Dashboard > Agents > your agent > Deploy tab. Copy the 2-line script snippet. Paste it before the closing </body> tag on your website. Works on WordPress (use a custom HTML block), Shopify (paste in theme.liquid), Wix (use custom code in site settings), Squarespace (use code injection), or any custom HTML site.

Q: "Is my data safe?"
A: Yes. Encrypted in transit (TLS 1.3) and at rest (AES-256). We never sell data. We never train AI on your conversations. You can export or delete all your data anytime in line with GDPR data portability and deletion rights. PII Firewall tokenises sensitive data before it reaches AI models.

Q: "Do I need to download anything?"
A: No. Aditya Labs is fully web-based. Your customers chat through a web widget — no downloads needed. For our separate Aditya Lens desktop product, a download is required for local PII scanning.

Q: "What AI model does it use?"
A: GPT-4o-mini by default (OpenAI). You can switch to Groq, Mistral, Cohere, Anthropic, or any OpenAI-compatible endpoint via the BYOAI feature in settings.

Q: "Can I try before buying?"
A: Yes. Free plan (50 messages/month, 1 agent) — no credit card required. Growth includes a 14-day free trial, and annual billing lowers the monthly price on higher tiers.

Q: "Do you support my country?"
A: We serve businesses worldwide. The product is English-first today, with multilingual support still in development. We are designed to support privacy programs in the UK, EU, Canada, California, India, and other jurisdictions, but each customer should review their own legal and operational obligations before go-live.

### SAFETY RULES ###
- NEVER fabricate features, pricing, or capabilities. If unsure, say "I'm not sure — I can guide you to our Contact Us form so the team can follow up safely."
- NEVER claim HIPAA certification. Say "PII-protected design — we stay outside HIPAA scope" and explain we do not collect or store PHI.
- NEVER claim SOC 2 certification for Aditya Labs. Attribute it to infrastructure providers.
- NEVER promise guaranteed ROI or results. Use "industry projections" or "research suggests."
- NEVER collect credit card numbers, passwords, or government IDs.
- If the visitor seems upset, frustrated, or asks for a refund, immediately offer Contact Us.
- Always end with a clear next step: sign up free, book a demo, or use Contact Us.

### DEPLOYMENT & TROUBLESHOOTING GUIDE ###

SHOPIFY:
- Widget code goes in Online Store > Themes > Edit Code > theme.liquid > paste before </body>
- Common error: "Widget not showing" — Clear Shopify cache, check if code is BEFORE </body> not after
- Common error: "Agent not responding" — Verify agentId matches your agent's ID from the Deploy tab
- Theme conflicts: Some themes block third-party scripts. Try adding to a Custom Liquid section instead.

WORDPRESS:
- Method 1: Appearance > Theme File Editor > footer.php > paste before </body>
- Method 2: Install "Insert Headers and Footers" plugin > paste in Footer Scripts
- Common error: "Code disappeared after theme update" — Use the plugin method, it survives theme updates
- Common error: "Widget shows on admin but not live site" — Clear all caches (WP cache, CDN, browser)

WIX:
- Settings > Custom Code > Add Custom Code > Paste widget code > Body end > All pages
- Common error: "Code not accepted" — Make sure you include BOTH script tags (config + loader)
- Preview won't show it — you must publish first

SQUARESPACE:
- Settings > Advanced > Code Injection > Footer > Paste code > Save
- Only available on Business plan or higher (not Personal plan)

CUSTOM HTML:
- Open your HTML file > Find </body> tag > Paste widget code ABOVE it > Save & upload
- If using a template/CMS: Look for "footer" or "scripts" section

GENERAL TROUBLESHOOTING:
- Widget not appearing: Check browser console (F12 > Console) for red errors
- "Failed to load resource": Check your internet connection or if adityalabs.ai is accessible
- Agent gives wrong answers: Update your Knowledge Base with correct information
- Agent too slow: This is normal on first load (cold start). Subsequent messages are faster.
- "Unauthorized" error: Your agent may be set to "draft" status — change to "active"

ACCOUNT & LOGIN:
- Can't log in: Try Google Sign-In (we use Google Auth)
- Forgot which email: Check your browser's saved passwords
- Account locked: Wait 15 minutes or use Contact Us.
- Can't create agent: Check your plan limits in Billing page

KNOWLEDGE BASE:
- "Import from Website" scrapes your site's text content (About, Services, FAQ pages work best)
- After importing, wait for "RAG-Ready" status — embedding takes 1-2 minutes
- Quality Score below 50: Add more specific Q&A pairs, avoid vague content
- "Embedding failed": Content may be too long — split into smaller entries

INTEGRATIONS:
- Google Calendar not syncing: Re-authorize in Settings > Integrations > Google Calendar > Verify
- Gmail not sending: Check if you've granted "Send email" permission during OAuth
- Webhooks not firing: Verify your endpoint URL accepts POST requests and returns 200

Always be helpful, specific, and guide the user step by step. If you can't solve the issue, suggest they use Contact Us with their agent ID and a screenshot of the error.`;

const FALLBACK_GREETING = buildPrivacyGreeting("", "compact");

// ── Popup messages that type out to grab attention ──────────────────
// Kept short so the teaser bubble does not obscure hero copy or CTAs.
// No claims, no urgency language — invites a click and nothing more.
const POPUP_MESSAGES = [
  "Ask how an AI agent could help.",
  "Curious how an AI agent could help? Ask me.",
  "See how an AI agent could help. Ask me.",
];

const DASHBOARD_POPUP_MESSAGES = [
  "Need help? I can guide you through anything on this page.",
  "Stuck on something? Ask me — I know this platform inside out.",
  "Hi! I can help you create agents, deploy widgets, and more.",
];

// ── Types ───────────────────────────────────────────────────────────
interface ChatMessage {
  role: "user" | "assistant";
  content: string;
  action?: ContactUsActionMetadata;
}

interface SalesAgentConfig {
  id: string;
  name: string;
  system_prompt: string;
  greeting: string;
}


const QUICK_REPLIES = [
  "I'm a Lawyer",
  "I'm a Dentist",
  "I'm a Realtor",
  "I run a Restaurant",
  "I run a Gym",
  "Contact Us",
];

const DASHBOARD_QUICK_REPLIES = [
  "How do I create an agent?",
  "How do I deploy?",
  "Add knowledge base",
  "Upgrade my plan",
  "Widget not showing",
  "Contact Us",
];

// Map quick replies to industry niche pages
const INDUSTRY_PAGES: Record<string, string> = {
  "I'm a Lawyer": "/law-firms",
  "I'm a Dentist": "/dental",
  "I'm a Realtor": "/real-estate",
  "I run a Restaurant": "/restaurants",
  "I run a Gym": "/fitness",
};

// No session storage — each visit starts fresh with greeting + quick replies

// ── Component ───────────────────────────────────────────────────────
const DASHBOARD_PREFIXES = ["/dashboard", "/agents", "/deploy", "/settings", "/billing", "/knowledge", "/analytics", "/conversations", "/leads", "/integrations", "/templates", "/onboarding", "/widget", "/privacy-generator", "/voice", "/email", "/marketplace", "/are", "/funnel", "/deal", "/ticket", "/notes", "/report", "/smart-routing", "/ai-metrics", "/help-center", "/welcome"];

const DASHBOARD_GREETING = buildPrivacyGreeting("", "compact");

export default function SupportChat() {
  const pathname = usePathname();
  const isDashboard = DASHBOARD_PREFIXES.some((p) => pathname.startsWith(p));
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [salesAgent, setSalesAgent] = useState<SalesAgentConfig | null>(null);

  // ── Popup state (static — no wiggle/glow/bounce animations) ──────
  // Animations removed to align with "no fake urgency" honesty brand.
  // Chat button is always visible and static — users click when ready.
  const [showPopup, setShowPopup] = useState(false);
  const [typedText, setTypedText] = useState("");
  const [popupDismissed, setPopupDismissed] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const popupTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Mount guard ─────────────────────────────────────────────────
  useEffect(() => {
    setMounted(true);
  }, []);

  // ── Overlay-aware placement ─────────────────────────────────────
  // The widget sits bottom-right at z-[95]; other fixed elements
  // (cookie banner, guided-tour overlay, wizard flows) can crowd it.
  // Priority — reposition up first, minimize second, hide only if a
  // critical overlay is active.
  //
  // Wizard routes: collapse the attention popup so the 56px button is
  // all the user sees, leaving room for Next / Launch buttons.
  const isWizardRoute =
    pathname.startsWith("/agents/new") ||
    pathname.startsWith("/onboarding") ||
    pathname.startsWith("/welcome");

  // Homepage: the hero copy, trust strip, and CTAs already fill the
  // first viewport at common laptop heights. The teaser bubble is
  // suppressed entirely on "/" — no timer, no popup, no JSX — so it
  // cannot obstruct hero text under any circumstance. The blue chat
  // coin remains visible immediately and stays clickable; visitors
  // can still open the assistant on demand.
  const isHomepage = pathname === "/";

  const [bottomOffset, setBottomOffset] = useState(16); // matches bottom-4
  const [forceHidden, setForceHidden] = useState(false);

  useEffect(() => {
    if (!mounted) return;

    const recompute = () => {
      // Wizard routes need the full bottom-right for the sticky Next /
      // Launch button — hide the widget completely so it cannot obscure
      // primary actions.
      if (isWizardRoute) {
        setForceHidden(true);
        setBottomOffset(16);
        return;
      }
      const tourActive = document.querySelector<HTMLElement>(
        '[data-overlay="tour-active"]'
      );
      if (tourActive) {
        setForceHidden(true);
        setBottomOffset(16);
        return;
      }
      setForceHidden(false);

      const cookieBanner = document.querySelector<HTMLElement>(
        '[data-overlay="cookie-banner"]'
      );
      if (cookieBanner) {
        const rect = cookieBanner.getBoundingClientRect();
        // Push the widget above the banner with an 8px gap.
        const lift = Math.max(0, window.innerHeight - rect.top) + 8;
        setBottomOffset(Math.max(16, lift));
      } else {
        setBottomOffset(16);
      }
    };

    recompute();
    const observer = new MutationObserver(recompute);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["data-overlay", "class", "style"],
    });
    window.addEventListener("resize", recompute);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", recompute);
    };
  }, [mounted, isWizardRoute]);

  // ── Load Sales Agent config ─────────────────────────────────
  // Uses hardcoded fallback directly to avoid RLS/406 errors from
  // anonymous Supabase queries on public pages. The fallback prompts
  // are always used anyway (lines 292-296 above used FALLBACK_*).
  useEffect(() => {
    if (!mounted) return;
    setSalesAgent({
      id: "support-chat-public",
      name: "Aditya Labs Sales Agent",
      system_prompt: FALLBACK_SYSTEM_PROMPT,
      greeting: FALLBACK_GREETING,
    });
  }, [mounted]);

  // ── Quiet popup — shows once after 8s, no wiggle/glow/bounce ──
  // Respects user attention: appears gently, dismisses permanently.
  // Suppressed entirely on:
  //   - wizard routes (/agents/new, /onboarding, /welcome): so it
  //     never sits on a sticky Next / Launch / Skip primary action,
  //   - homepage ("/"): so it cannot obstruct hero copy, trust strip,
  //     CTAs, or the "Designed for small businesses..." paragraph.
  // On every other public page and on the dashboard the popup follows
  // its standard 8-second post-mount timer.
  useEffect(() => {
    if (!mounted || isOpen || popupDismissed || isWizardRoute || isHomepage) return;

    popupTimerRef.current = setTimeout(() => {
      const msgs = isDashboard ? DASHBOARD_POPUP_MESSAGES : POPUP_MESSAGES;
      const message = msgs[Math.floor(Math.random() * msgs.length)];
      setTypedText(message);
      setShowPopup(true);
    }, 8000);

    return () => {
      if (popupTimerRef.current) clearTimeout(popupTimerRef.current);
    };
  }, [mounted, isOpen, popupDismissed, isDashboard, isWizardRoute, isHomepage]);

  // ── Auto-scroll on new messages ─────────────────────────────────
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  // ── Focus input when chat opens ─────────────────────────────────
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 200);
    }
  }, [isOpen]);

  // ── Open chat handler (adds greeting if no messages) ────────────
  const handleOpen = useCallback(() => {
    setIsOpen(true);
    setShowPopup(false);
    setPopupDismissed(true);
    if (popupTimerRef.current) clearTimeout(popupTimerRef.current);
    setMessages((prev) => {
      if (prev.length === 0) {
        return [
          {
            role: "assistant" as const,
            content: isDashboard ? DASHBOARD_GREETING : (salesAgent?.greeting || FALLBACK_GREETING),
          },
        ];
      }
      return prev;
    });
  }, [salesAgent, isDashboard]);

  // ── Dismiss popup (X button) ──────────────────────────────────
  const handleDismissPopup = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      setShowPopup(false);
      setPopupDismissed(true);
      if (popupTimerRef.current) clearTimeout(popupTimerRef.current);
    },
    []
  );

  // ── Send message ────────────────────────────────────────────────
  const sendMessage = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || loading) return;

      const userMessage: ChatMessage = { role: "user", content: trimmed };
      setMessages((prev) => [...prev, userMessage]);
      setInput("");

      const privacyReply = getSensitiveDataGuardReplyWithContext(trimmed, {
        agentName: salesAgent?.name || "Aditya Labs Sales Agent",
        systemPrompt: salesAgent?.system_prompt || FALLBACK_SYSTEM_PROMPT,
      });
      if (privacyReply) {
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant" as const,
            content: privacyReply.reply,
            action: privacyReply.metadata,
          },
        ]);
        return;
      }

      const supportReply = getSupportHelpReply(trimmed);
      if (supportReply) {
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant" as const,
            content: supportReply,
            ...(supportReply === CONTACT_US_HANDOFF_REPLY && {
              action: CONTACT_US_ACTION_METADATA,
            }),
          },
        ]);
        return;
      }

      // Instant response for human-handoff intents — no API call needed.
      // Do not ask the visitor to request follow-up in chat;
      // route them to the Contact Us form instead.
      if (
        trimmed.toLowerCase().includes("human") ||
        trimmed.toLowerCase().includes("talk to a person") ||
        trimmed.toLowerCase().includes("talk to someone") ||
        trimmed.toLowerCase().includes("speak to someone") ||
        trimmed.toLowerCase().includes("contact us")
      ) {
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant" as const,
            content:
              "I can guide you to our Contact Us form so the team can follow up safely.",
            action: CONTACT_US_ACTION_METADATA,
          },
        ]);
        return;
      }

      const fallbackReply = getSupportFallbackReply(trimmed);
      if (fallbackReply) {
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant" as const,
            content: fallbackReply,
          },
        ]);
        return;
      }

      setLoading(true);

      try {
        const res = await fetch("/api/chat", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            messages: [...messages, userMessage].map((m) => ({
              role: m.role,
              content: m.content,
            })),
            systemPrompt: salesAgent?.system_prompt || FALLBACK_SYSTEM_PROMPT,
            agentName: salesAgent?.name || "Aditya Labs Sales Agent",
            agentId: salesAgent?.id || "support-widget",
          }),
        });

        if (!res.ok) throw new Error("API error");

        const data = await res.json();
        const assistantMessage: ChatMessage = {
          role: "assistant",
          content:
            data.message ||
            "I'm having a brief connection issue. Please use Contact Us or sign up for our free plan at adityalabs.ai/signup!",
          action: data.metadata,
        };
        setMessages((prev) => [...prev, assistantMessage]);
      } catch {
        const fallback: ChatMessage = {
          role: "assistant",
          content:
            "I'm having a brief connection issue. Please use Contact Us or sign up for our free plan at adityalabs.ai/signup!",
        };
        setMessages((prev) => [...prev, fallback]);
      } finally {
        setLoading(false);
      }
    },
    [messages, loading, salesAgent]
  );

  // ── Handle form submit ──────────────────────────────────────────
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    sendMessage(input);
  };

  // ── Quick reply handler ─────────────────────────────────────────
  const handleQuickReply = (text: string) => {
    sendMessage(text);
  };

  // ── Show on ALL pages — public and dashboard ─────────────────────
  // AI helper is available everywhere: sales assistant on public pages,
  // help assistant on dashboard pages. The chat coin renders on the
  // first paint without waiting for the mount-effect — previously the
  // `!mounted` early return delayed the coin by a hydration tick,
  // which read as "the coin appears a few seconds late". The state
  // that depends on client APIs (forceHidden, bottomOffset) defaults
  // to safe values on first render, so removing the mounted gate is
  // safe.
  // Exception: /widget/* pages (embeddable agent) — no nested chat
  // widget. Wizard routes are also hidden synchronously so the coin
  // never flashes in over a sticky Next / Launch button.
  if (pathname.startsWith("/widget/")) return null;
  if (isWizardRoute) return null;
  if (forceHidden) return null;

  // ── Render ──────────────────────────────────────────────────────
  return (
    <>
      {/* ── Chat Window ──────────────────────────────────────── */}
      {isOpen && (
        <div
          className="fixed right-4 z-[95] flex flex-col
            w-[calc(100vw-2rem)] sm:w-[380px] h-[520px]
            rounded-2xl shadow-2xl overflow-hidden
            bg-white dark:bg-gray-900
            border border-gray-200 dark:border-gray-700"
          style={{
            bottom: bottomOffset,
            animation: "supportChatSlideUp 0.3s ease-out",
          }}
        >
          {/* ── AI Disclosure Banner — EU AI Act Article 50 ── */}
          <div className="bg-amber-50 px-3 py-1.5 text-center shrink-0 border-b border-amber-200">
            <div className="flex items-center justify-center gap-2 text-[10px] text-amber-700 leading-tight">
              <Bot className="h-3.5 w-3.5 text-[#D4AF37]" aria-hidden="true" />
              <span>
                You are chatting with an <strong>AI assistant</strong>, not a human. ·{" "}
                <a href="/contact" className="underline hover:text-amber-900 font-medium">Contact Us</a>
              </span>
            </div>
          </div>

          {/* ── Header ─────────────────────────────────────── */}
          <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white shrink-0">
            <div className="flex items-center gap-2">
              <div className="relative">
                <PrimarySymbol size={32} className="w-8 h-8" />
                {/* Online indicator */}
                <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-400 border-2 border-purple-600 rounded-full" />
              </div>
              <div>
                <p className="text-sm font-semibold leading-tight">
                  {isDashboard ? "AI Helper" : "Aditya — AI Assistant"}
                </p>
                <p className="text-[11px] text-blue-100 leading-tight flex items-center gap-1">
                  <span className="w-1.5 h-1.5 bg-green-400 rounded-full inline-block animate-pulse" />
                  {isDashboard ? "Ask me anything about this page" : "Online — replies instantly"}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1">
            {/* New Chat (reset) button — only show after user has chatted */}
            {messages.length > 1 && (
              <button
                onClick={() => {
                  setMessages([
                    {
                      role: "assistant" as const,
                      content: isDashboard ? DASHBOARD_GREETING : (salesAgent?.greeting || FALLBACK_GREETING),
                    },
                  ]);
                  setInput("");
                }}
                className="p-1 rounded-full hover:bg-white/20 transition-colors"
                aria-label="New chat"
                title="Start new conversation"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
            )}
            <button
              onClick={() => setIsOpen(false)}
              className="p-1 rounded-full hover:bg-white/20 transition-colors"
              aria-label="Close chat"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
            </div>
          </div>

          {/* ── Messages ───────────────────────────────────── */}
          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 bg-gray-50 dark:bg-gray-950">
            {messages.map((msg, i) => (
              <div
                key={i}
                className={`flex ${
                  msg.role === "user" ? "justify-end" : "justify-start"
                }`}
              >
            <div
              className={`max-w-[80%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                msg.role === "user"
                  ? "bg-blue-600 text-white rounded-br-md"
                  : "bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 rounded-bl-md shadow-sm border border-gray-100 dark:border-gray-700"
              }`}
            >
              {msg.content}
              {msg.role === "assistant" && msg.action && msg.action.action === "contact_us" && (
                <ContactUsAction
                  href={msg.action.href}
                  label={msg.action.label}
                  className="mt-3 border-blue-200 text-blue-700 hover:bg-blue-50 hover:text-blue-800 focus-visible:ring-blue-500"
                />
              )}
            </div>
          </div>
            ))}

            {/* ── Quick Replies (only after greeting, before user sends) */}
            {messages.length === 1 &&
              messages[0].role === "assistant" &&
              !loading && (
                <div className="flex flex-wrap gap-2 pt-1">
                  {(isDashboard ? DASHBOARD_QUICK_REPLIES : QUICK_REPLIES).map((qr) => (
                    <button
                      key={qr}
                      onClick={() => handleQuickReply(qr)}
                      className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                        qr === "Contact Us"
                          ? "border-green-200 dark:border-green-700 text-green-600 dark:text-green-400 bg-white dark:bg-gray-800 hover:bg-green-50 dark:hover:bg-gray-700"
                          : "border-blue-200 dark:border-blue-700 text-blue-600 dark:text-blue-400 bg-white dark:bg-gray-800 hover:bg-blue-50 dark:hover:bg-gray-700"
                      }`}
                    >
                      {qr}
                    </button>
                  ))}
                </div>
              )}

            {/* ── Industry page link (after user picks an industry) */}
            {messages.length >= 2 && (() => {
              const lastUserMsg = [...messages].reverse().find(m => m.role === "user");
              const industryPage = lastUserMsg ? INDUSTRY_PAGES[lastUserMsg.content] : null;
              return industryPage ? (
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl px-3 py-2">
                  <a
                    href={industryPage}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-xs text-blue-700 dark:text-blue-300 font-medium hover:underline"
                  >
                    <FileText className="h-3.5 w-3.5 text-[#D4AF37]" aria-hidden="true" />
                    <span>See how AI works for your industry →</span>
                  </a>
                </div>
              ) : null;
            })()}

            {/* ── Typing indicator ──────────────────────────── */}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-white dark:bg-gray-800 rounded-2xl rounded-bl-md px-4 py-3 shadow-sm border border-gray-100 dark:border-gray-700">
                  <div className="flex items-center gap-1">
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce [animation-delay:0ms]" />
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce [animation-delay:150ms]" />
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce [animation-delay:300ms]" />
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* ── Input Area ─────────────────────────────────── */}
          <form
            onSubmit={handleSubmit}
            className="flex flex-col gap-1 px-3 py-3 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shrink-0"
          >
            <div className="flex items-center gap-2">
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Type a message..."
                aria-label="Type a message to the support agent"
                disabled={loading}
                className="flex-1 text-sm px-3 py-2 rounded-full border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50"
              />
              <button
                type="submit"
                disabled={loading || !input.trim()}
                className="p-2 rounded-full bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
                aria-label="Send message"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M5 12h14M12 5l7 7-7 7"
                  />
                </svg>
              </button>
            </div>
            <p className="text-center text-[9px] text-gray-400">
              By chatting, you agree to our{" "}
              <a href="/privacy" className="underline hover:text-gray-500">Privacy Policy</a>
            </p>
          </form>
        </div>
      )}

      {/* ── Floating Chat Button + Animated Popup ──────────── */}
      {/*
        The teaser bubble and the chat coin are independent fixed-positioned
        elements (no shared flex wrapper). The teaser sits horizontally to
        the LEFT of the coin (right: 80px = coin-right-4 + coin-w-56 + 8px
        gap + 12px buffer) and only renders on xl+ screens. This keeps it
        out of the hero text area on common laptop widths and removes any
        chance of it sliding upward into the headline / CTAs.
      */}
      {!isOpen && (
        <>
          {/*
            Premium translucent-glass teaser. supports-[backdrop-filter]
            gives modern browsers the truly light 35% navy fill; the 65%
            fallback (no blur) stays legible without the blur effect.
            Hidden until xl (≥1280px) — phones and tablets never see it.
          */}
          {/*
            Defense-in-depth: even though the popup effect never sets
            showPopup=true on the homepage, also gate the JSX on
            !isHomepage so a future regression in the effect cannot
            sneak a teaser onto "/".
          */}
          {showPopup && !popupDismissed && !isHomepage && (
            <div
              className="hidden xl:block fixed z-[95] max-w-[220px] cursor-pointer rounded-2xl px-3 py-2 border border-white/15 ring-1 ring-white/10 shadow-[0_16px_40px_rgba(0,0,0,0.25)] backdrop-blur-2xl backdrop-saturate-150 bg-slate-900/65 supports-[backdrop-filter]:bg-slate-900/35 animate-fadeIn"
              style={{ right: 80, bottom: bottomOffset + 6 }}
              onClick={handleOpen}
            >
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDismissPopup(e);
                }}
                aria-label="Dismiss"
                data-testid="support-popup-dismiss"
                className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-white/95 text-[#0B1226] flex items-center justify-center text-[11px] font-semibold shadow hover:bg-white transition-colors"
              >
                ✕
              </button>

              <p className="text-white/90 leading-snug pr-2 text-[13px]">
                {typedText}
              </p>
            </div>
          )}

          {/*
            Chat coin. The "AI" label is BAKED INTO the button content as a
            stacked icon-over-wordmark composition, both centered inside
            the 56x56 disc. Nothing escapes the coin bounds — no floating
            badge can ever overlap the teaser or page content.
          */}
          <button
            onClick={handleOpen}
            aria-label={isDashboard ? "Open AI Helper" : "Chat with AI assistant"}
            style={{ bottom: bottomOffset }}
            className="fixed right-4 z-[95] w-14 h-14 rounded-full bg-[#4361ee] text-white shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-200 flex flex-col items-center justify-center shrink-0 gap-0.5"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.75} stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75 0 2.186.72 4.205 1.935 5.832L3 21.75l3.918-1.185A9.716 9.716 0 0012 21.75c5.385 0 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25z" />
            </svg>
            <span
              aria-hidden="true"
              className="text-[9px] font-bold tracking-[0.12em] leading-none text-white/95"
            >
              AI
            </span>
          </button>
        </>
      )}
    </>
  );
}
